// Admin → Queue's wording and sort values (owner ruling 7 of 2026-09-28; ADR 0013). Pure, so the page's
// words are tested without a browser. Nothing here decides access; the server has answered already.
import type { QueueRunningJobDto } from '../../shared/api-types.js';
import { isOneOf, JOB_KIND_LABELS, JOB_KINDS, JOB_WAIT_REASON_LABELS, QUEUE_RUN_STATES, type JobWaitReason, type QueueRunState } from '../../shared/enums.js';
import { reasonInWords } from '../pages/lib/activity.js';
import { formatCount, formatDuration, type Tone } from '../ui/index.js';

/** A kind's label; a kind this build does not know (a row left by another build) is named as such. */
export function jobKindLabel(kind: string): string {
  return isOneOf(JOB_KINDS, kind) ? JOB_KIND_LABELS[kind] : `Unknown kind (${kind})`;
}

/** The kinds in their declared order, unknown ones last. */
export function jobKindRank(kind: string): number {
  return isOneOf(JOB_KINDS, kind) ? JOB_KINDS.indexOf(kind) : JOB_KINDS.length;
}

/**
 * What kind of work it is, as the claim orders it: a person's press ("Refresh now") first, then live work
 * (syncs, new meetings, requests), then bulk work (a refresh of recent meetings).
 */
export function workLabel(row: { bulk: boolean; priority: number }): string {
  if (row.priority === 1) return 'Asked for now';
  return row.bulk ? 'Bulk (a refresh)' : 'Live';
}

export function workRank(row: { bulk: boolean; priority: number }): number {
  if (row.priority === 1) return 0;
  return row.bulk ? 2 : 1;
}

/**
 * The status line above the queue: when it was read once it has been, "Loading the queue…" only while the first read is
 * on its way, and nothing once that read has failed or been refused — the failure beneath owns the space (app-baseline
 * 05-client §5.4: a failed dependency never leaves an indefinite loading state; Phase 1c finding D18).
 */
export function queueStatusLine(read: { asOf: string; zone: string } | null, status: 'loading' | 'ready' | 'error'): string {
  if (read) return `As of ${read.asOf} (${read.zone}). Jobs appear by kind and person, never by what they are about.`;
  return status === 'loading' ? 'Loading the queue…' : '';
}

/** "Attempt 2 of 3". */
export function attemptText(attempts: number, maxAttempts: number | null): string {
  return maxAttempts === null ? `Attempt ${formatCount(attempts)}` : `Attempt ${formatCount(attempts)} of ${formatCount(maxAttempts)}`;
}

/** A duration in human units, from milliseconds (04-data §4). */
export function durationText(ms: number | null | undefined): string {
  return formatDuration(ms === null || ms === undefined ? null : ms / 1000);
}

/**
 * Why the first job of a group waits, in the claim's words. With no rule holding it, the next claim takes
 * it — within a second, or as soon as the job ahead of it in the same claim is placed. Without a worker in
 * the serving process nobody can say, and the page says so rather than guess.
 */
export function waitReasonText(reason: JobWaitReason | null, workerInThisProcess: boolean): string {
  if (!workerInThisProcess) return '—';
  return reason === null ? 'Starts at the next claim' : JOB_WAIT_REASON_LABELS[reason];
}

/** Waits by reason in the claim rules' precedence; "starts next" first, unknown last. */
export function waitReasonRank(reason: JobWaitReason | null, workerInThisProcess: boolean): number {
  if (!workerInThisProcess) return 99;
  return reason === null ? -1 : Object.keys(JOB_WAIT_REASON_LABELS).indexOf(reason);
}

export const RUN_STATE_TONES: Readonly<Record<QueueRunState, Tone>> = {
  running: 'info',
  overran: 'warning',
  orphaned: 'warning',
  other_process: 'neutral',
  abandoned: 'danger',
};

/** What each state means, for the pill's title and the card. */
export const RUN_STATE_HINTS: Readonly<Record<QueueRunState, string>> = {
  running: 'Run by one of this process\'s slots.',
  overran: 'Run by one of this process\'s slots past its lease: slow or hung. It keeps its slot rather than run twice.',
  orphaned: 'Claimed by this process, but no slot runs it (its settlement failed). It is claimed again when its lease ends.',
  other_process: 'Held by another worker process, within its lease.',
  abandoned: 'Held by another process past its lease — that process has stopped. The next claim takes it over.',
};

export function runStateRank(state: QueueRunState): number {
  return QUEUE_RUN_STATES.indexOf(state);
}

/** "2 of 3". */
export function ofText(used: number, cap: number): string {
  return `${formatCount(used)} of ${formatCount(cap)}`;
}

/**
 * Why jobs of a kind were Skipped (05-content §6: "a skipped run SHOULD carry its reason beside the word"), as a phrase
 * starting with a capital. Why the record pages' words (pages/lib/activity.ts): one code is never worded two ways, and a
 * code with no words of its own is spelt out from its key, never shown raw.
 */
export function skipReasonText(code: string): string {
  const words = reasonInWords(code);
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A running job's heartbeat age, for sorting the oldest first. */
export function heartbeatMs(row: Pick<QueueRunningJobDto, 'lastHeartbeatAt'>): number | null {
  return row.lastHeartbeatAt === null ? null : Date.parse(row.lastHeartbeatAt);
}
